import * as React from 'react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { triggerWrap as triggerCls, bubble as bubbleCls } from './Tooltip.css';

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  /** Content rendered inside the tooltip bubble. ReactNode for inline JSX. */
  content: React.ReactNode;
  side?: TooltipSide;
  /** Show delay in ms. Default 300. Hide is 0. */
  delay?: number;
  /** Hide the tooltip entirely (e.g. for accessibility toggles). */
  disabled?: boolean;
  /** Custom class merged onto the bubble — for tone overrides. */
  className?: string;
  /** Element wrapped by the tooltip; gains `aria-describedby` while open. */
  children: React.ReactElement;
}

/**
 * Tooltip — portal-rendered hover/focus bubble. Wraps any single React
 * element; the wrapped child becomes the anchor and receives
 * `aria-describedby` while the tooltip is visible.
 *
 * - 300ms show-delay (configurable), 0ms hide.
 * - Mouse enter / focus shows; mouse leave / blur hides.
 * - Auto-positions above the anchor by default; pass `side` to flip.
 *
 * Prototype: `http://localhost:8000/` tooltip pattern (dark `#0f172a`
 * bubble, 12px radius, white 12/400 text).
 *
 * **The child MUST be focusable for keyboard / screen-reader support.**
 * Show/hide is wired via `onFocus` / `onBlur` on the wrapper `<span>`,
 * which only fire when focus actually lands on (or inside) the wrapped
 * element. A non-focusable child (plain text, a `<div>`, a `disabled`
 * `<button>`) is therefore *hover-only* — keyboard users never see the
 * tip and AT never reaches the `aria-describedby` target. Wrap an
 * interactive element (`Button`, `IconButton`, a link, an input) or give
 * the child `tabIndex={0}`. In dev a `console.warn` fires once per anchor
 * if the wrapped element resolves to a non-focusable node.
 *
 * **Positioning model (vs `Popover`).** Tooltip positions
 * `position: absolute` in document-flow coordinates — it adds
 * `window.scrollX/Y` to the viewport rect once, on open, and does **not**
 * subscribe to scroll/resize. This is fine for a hover/focus hint that
 * dismisses the moment the pointer/focus leaves (so it can't visibly
 * drift mid-scroll). `Popover`, by contrast, uses `position: fixed` and
 * re-measures on scroll + resize while open because it stays open across
 * such events. The divergence is intentional; don't unify them blindly.
 */

interface Position {
  top: number;
  left: number;
}

function computePosition(anchor: DOMRect, bubble: DOMRect, side: TooltipSide, gap = 6): Position {
  switch (side) {
    case 'top':
      return {
        top: anchor.top - bubble.height - gap,
        left: anchor.left + anchor.width / 2 - bubble.width / 2,
      };
    case 'bottom':
      return {
        top: anchor.bottom + gap,
        left: anchor.left + anchor.width / 2 - bubble.width / 2,
      };
    case 'left':
      return {
        top: anchor.top + anchor.height / 2 - bubble.height / 2,
        left: anchor.left - bubble.width - gap,
      };
    case 'right':
      return {
        top: anchor.top + anchor.height / 2 - bubble.height / 2,
        left: anchor.right + gap,
      };
  }
}

export function Tooltip({
  content,
  side = 'top',
  delay = 300,
  disabled = false,
  className,
  children,
}: TooltipProps) {
  const id = useId();
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Position | null>(null);

  const show = useCallback(() => {
    if (disabled) return;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setOpen(true), delay);
  }, [disabled, delay]);

  const hide = useCallback(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    setOpen(false);
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    [],
  );

  // Disabling mid-hover (e.g. `disabled={busy}` after a click) closes an open
  // tip and cancels a pending one — `show` alone only blocks new opens.
  useEffect(() => {
    if (disabled) hide();
  }, [disabled, hide]);

  // Dev-only: warn once if the wrapped child is not focusable, since the
  // tooltip would then be hover-only (no keyboard / AT access). Cheap —
  // one DOM read on mount, stripped from production builds.
  useEffect(() => {
    // A disabled tooltip never shows, so an unfocusable child (e.g. a
    // natively disabled button) isn't a problem yet — re-check on enable.
    if (process.env['NODE_ENV'] === 'production' || disabled) return;
    const wrapper = anchorRef.current;
    const child = wrapper?.firstElementChild as HTMLElement | null;
    if (!child) return;
    const focusable =
      child.matches(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) || child.isContentEditable;
    if (!focusable) {
      console.warn(
        '[Tooltip] The wrapped child is not focusable, so the tooltip is ' +
          'hover-only and unreachable by keyboard / screen-reader users. ' +
          'Wrap a focusable element (Button, link, input) or add tabIndex={0}.',
      );
    }
  }, [disabled]);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current || !bubbleRef.current) return;
    const a = anchorRef.current.getBoundingClientRect();
    const b = bubbleRef.current.getBoundingClientRect();
    const p = computePosition(a, b, side);
    // Clamp to the viewport on both axes so a bubble wider/taller than its
    // anchor's distance from an edge (e.g. a long label on a rail pinned to
    // the window edge, or a `side="top"` tip near the page's top) doesn't
    // render partly off-screen — it slides to stay fully visible instead of
    // staying centred on the anchor. `Math.max(viewportSize - size - margin,
    // margin)` keeps the upper bound >= margin even when the bubble is wider
    // than the viewport itself, so the result is never pushed negative again.
    const margin = 4;
    const clamp = (value: number, size: number, viewportSize: number) =>
      Math.min(Math.max(value, margin), Math.max(viewportSize - size - margin, margin));
    const left = clamp(p.left, b.width, window.innerWidth);
    const top = clamp(p.top, b.height, window.innerHeight);
    setPos({ top: top + window.scrollY, left: left + window.scrollX });
  }, [open, side, content]);

  const bubble =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={bubbleRef}
            id={id}
            role="tooltip"
            className={[bubbleCls, className].filter(Boolean).join(' ')}
            data-shown={pos ? 'true' : 'false'}
            style={{
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            {content}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <span
        ref={anchorRef}
        className={triggerCls}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-describedby={open ? id : undefined}
      >
        {children}
      </span>
      {bubble}
    </>
  );
}
Tooltip.displayName = 'Tooltip';
